import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import {
  listEngines, getSchema, getTemplates, previewCreate, applyCreate, previewPatch, applyPatch,
  activateEngine, pauseEngine, resumeEngine, deleteEngine, setEngineManagement, setEngineMode, retireEngine,
  V2Engine, V2Schema, V2Template, V2Requires, V2Strategy, V2ApiError,
} from '../../services/optionsTraderV2Service';
import { canAccessAutoTrader } from '../../lib/entitlements';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';
import AlertSwitches from './AlertSwitches';
import ManualEngineCreate from './ManualEngineCreate';
import { MULTIPLIER, strategyLabel, maskAcct, engineDisplayName } from './v2Shared';

// TT Options Trader v2 (paper) -- Engines. A copy of v1 OptionsEngines.tsx
// (same layout, labels and styles) backed by the /v2 engines API.
// Create/edit go through preview -> apply. Page load = 1 request (GET /v2/engines);
// schema + templates are fetched lazily when the form first opens.

const SYMBOLS = [
  { value: 'ES', label: 'ES (E-mini S&P 500)', multiplier: '$50/pt' },
  { value: 'MES', label: 'MES (Micro E-mini S&P 500)', multiplier: '$5/pt' },
  { value: 'SPX', label: 'SPX (S&P 500 Index)', multiplier: '$100/pt' },
  { value: 'SPY', label: 'SPY (S&P 500 ETF)', multiplier: '$100/pt' },
  { value: 'GC', label: 'GC (Gold Futures)', multiplier: '$100/oz' },
  { value: 'CL', label: 'CL (Crude Oil Futures)', multiplier: '$1000/bbl' },
];

const cardStyle: React.CSSProperties = { background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: '20px 24px', marginBottom: 12 };
const labelStyle: React.CSSProperties = { fontSize: 13, color: '#9ca3af', marginBottom: 4, display: 'block' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 6, background: '#111827', color: '#f9fafb', border: '1px solid #374151', fontSize: 14 };
const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { padding: '10px 24px', background: '#6d28d9', color: '#f9fafb', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const btnSecondary: React.CSSProperties = { ...btnPrimary, background: '#374151' };
const btnDanger: React.CSSProperties = { ...btnPrimary, background: '#991b1b', fontSize: 12, padding: '6px 12px' };
const btnSmall: React.CSSProperties = { padding: '4px 10px', borderRadius: 4, background: '#374151', color: '#f9fafb', border: 'none', fontSize: 12, cursor: 'pointer' };
const helpStyle: React.CSSProperties = { fontSize: 11, color: '#6b7280', marginTop: 2 };
const errorBanner: React.CSSProperties = { color: '#f87171', marginBottom: 12, padding: '8px 12px', background: '#7f1d1d22', borderRadius: 6 };
const warnBanner: React.CSSProperties = { color: '#fbbf24', marginBottom: 12, padding: '8px 12px', background: '#78350f22', borderRadius: 6, border: '1px solid #78350f' };

type TradeType = 'spread' | 'naked' | 'auto' | 'bear_call_spread' | 'jade_lizard' | 'iron_condor';
type ProfitCloseMode = 'smart' | 'strict';
type LossCloseMode = 'auto' | 'alerts';
type NotchesPreset = 'conservative' | 'balanced' | 'aggressive';

type FormState = {
  name: string; symbol: string; tradeType: TradeType; preset: NotchesPreset; minDte: number; maxDte: number;
  spreadWidth: number; quantity: number; maxOpenPositions: number;
  profitTargetPct: number; stopLossMultiplier: number; hardStopMultiplier: number;
  enableHardStopUnderlying: boolean; hardStopUnderlyingBuffer: number;
  maxDailyTrades: number; maxBpPct: number; minSpreadCredit: string;
  profitCloseMode: ProfitCloseMode; postProfitCooldownHours: number;
  lossCloseMode: LossCloseMode; stopLossMinDte: number;
  creditGateEnabled: boolean; creditGateMaxPct: number; creditGateOnBreach: 'skip' | 'proceed';
  // Advanced (v2-only)
  regimeGateEnabled: boolean;
  clpEnabled: boolean; clpThreshold: number;
  driftMaxUnderlyingMovePct: number; driftMaxDeltaIncrease: number; driftMaxLegRelSpread: number;
  minCreditToWidthPct: number; applyMinCredit: boolean;
  keyWindowMin: number; maxDataAgeMin: number; maxRetryDataAgeMin: number; observeWindowSec: number;
  fillWalkSteps: number;
};

// Mirrors api/engines-v2/lib/config/schema.json defaults + the notches templates.
const defaultForm: FormState = {
  name: '', symbol: 'ES', tradeType: 'spread', preset: 'conservative', minDte: 0, maxDte: 60,
  spreadWidth: 50, quantity: 1, maxOpenPositions: 3,
  profitTargetPct: 50, stopLossMultiplier: 2, hardStopMultiplier: 0,
  enableHardStopUnderlying: false, hardStopUnderlyingBuffer: 0.02,
  maxDailyTrades: 2, maxBpPct: 40, minSpreadCredit: '',
  profitCloseMode: 'smart', postProfitCooldownHours: 0,
  lossCloseMode: 'auto', stopLossMinDte: 21,
  creditGateEnabled: true, creditGateMaxPct: 1, creditGateOnBreach: 'skip',
  regimeGateEnabled: true, clpEnabled: true, clpThreshold: 3,
  driftMaxUnderlyingMovePct: 1.0, driftMaxDeltaIncrease: 0.05, driftMaxLegRelSpread: 0.2,
  minCreditToWidthPct: 0, applyMinCredit: true,
  keyWindowMin: 75, maxDataAgeMin: 75, maxRetryDataAgeMin: 4320, observeWindowSec: 20,
  fillWalkSteps: 3,
};

const PRESET_INFO: Record<NotchesPreset, { label: string; short: string }> = {
  conservative: { label: 'Conservative', short: 'Far-OTM, longer-DTE picks. Highest probability of profit, smaller credit per trade. Lowest risk.' },
  balanced: { label: 'Balanced', short: 'Mid-OTM, medium-DTE picks. Standard mix of credit and safety.' },
  aggressive: { label: 'Aggressive', short: 'Closer-to-ATM, shorter-DTE picks. Larger credit per trade, higher gamma risk near expiry.' },
};

const DEFAULT_MIN_CREDIT_DOLLARS: Record<string, number> = { SPX: 500, SPY: 50, ES: 100, MES: 25, GC: 150, CL: 100 };

// UI Trade Type -> v2 identity strategy + tradeType. Only naked_put supports
// tradeType spread|naked|auto; the other strategies trade the reading's own legs.
export const encodeTradeType = (ui: TradeType): { strategy: V2Strategy; tradeType: 'spread' | 'naked' | 'auto' } => {
  if (ui === 'bear_call_spread' || ui === 'jade_lizard' || ui === 'iron_condor') return { strategy: ui, tradeType: 'spread' };
  return { strategy: 'naked_put', tradeType: ui };
};
const decodeTradeType = (strategy: string, tradeType: string): TradeType =>
  strategy === 'bear_call_spread' || strategy === 'jade_lizard' || strategy === 'iron_condor'
    ? strategy
    : ((['spread', 'naked', 'auto'].includes(tradeType) ? tradeType : 'spread') as TradeType);

const n0 = (v: any, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);

function fromConfig(cfg: Record<string, any>, engine: V2Engine): FormState {
  const mult = MULTIPLIER[engine.symbol] || 5;
  const cdg = cfg.creditDegradationGate || {};
  const clp = cfg.consecutiveLossPause || {};
  const dg = cfg.driftGate || {};
  return {
    name: engine.name || '',
    symbol: engine.symbol || cfg.symbol || 'ES',
    tradeType: decodeTradeType(engine.strategy || cfg.strategy, cfg.tradeType),
    preset: (cfg.preset as NotchesPreset) || 'conservative',
    minDte: n0(cfg.minDte, 0), maxDte: n0(cfg.maxDte, 60),
    spreadWidth: n0(cfg.spreadWidth, 50), quantity: n0(cfg.quantity, 1), maxOpenPositions: n0(cfg.maxOpenPositions, 3),
    profitTargetPct: n0(cfg.profitTargetPct, 50), stopLossMultiplier: n0(cfg.stopLossMultiplier, 2), hardStopMultiplier: n0(cfg.hardStopMultiplier, 0),
    enableHardStopUnderlying: cfg.enableHardStopUnderlying === true, hardStopUnderlyingBuffer: n0(cfg.hardStopUnderlyingBuffer, 0.02),
    maxDailyTrades: n0(cfg.maxDailyTrades, 2), maxBpPct: n0(cfg.maxBpPct, 40),
    minSpreadCredit: cfg.minSpreadCredit != null ? String(Math.round(cfg.minSpreadCredit * mult)) : '',
    profitCloseMode: cfg.profitCloseMode === 'strict' ? 'strict' : 'smart',
    postProfitCooldownHours: n0(cfg.postProfitCooldownHours, 0),
    lossCloseMode: cfg.lossCloseMode === 'alerts' ? 'alerts' : 'auto',
    stopLossMinDte: n0(cfg.stopLossMinDte, 21),
    creditGateEnabled: cdg.enabled !== false, creditGateMaxPct: n0(cdg.maxPctBelowSignal, 1), creditGateOnBreach: cdg.onBreach === 'proceed' ? 'proceed' : 'skip',
    regimeGateEnabled: cfg.regimeGateEnabled !== false,
    clpEnabled: clp.enabled !== false, clpThreshold: n0(clp.threshold, 3),
    driftMaxUnderlyingMovePct: n0(dg.maxUnderlyingMovePct, 1.0), driftMaxDeltaIncrease: n0(dg.maxDeltaIncrease, 0.05), driftMaxLegRelSpread: n0(dg.maxLegRelSpread, 0.2),
    minCreditToWidthPct: n0(cfg.minCreditToWidthPct, 0), applyMinCredit: cfg.applyMinCredit !== false,
    keyWindowMin: n0(cfg.keyWindowMin, 75), maxDataAgeMin: n0(cfg.maxDataAgeMin, 75), maxRetryDataAgeMin: n0(cfg.maxRetryDataAgeMin, 4320), observeWindowSec: n0(cfg.observeWindowSec, 20),
    fillWalkSteps: n0(cfg.fillWalkSteps, 3),
  };
}

/** Form -> v2 config fields (no identity fields). */
export function toConfig(f: FormState): Record<string, any> {
  const { tradeType } = encodeTradeType(f.tradeType);
  return {
    tradeType, preset: f.preset, minDte: f.minDte, maxDte: f.maxDte,
    spreadWidth: f.spreadWidth, quantity: f.quantity, maxOpenPositions: f.maxOpenPositions,
    profitTargetPct: f.profitTargetPct, stopLossMultiplier: f.stopLossMultiplier, hardStopMultiplier: f.hardStopMultiplier,
    enableHardStopUnderlying: f.enableHardStopUnderlying, hardStopUnderlyingBuffer: f.hardStopUnderlyingBuffer,
    maxDailyTrades: f.maxDailyTrades, maxBpPct: f.maxBpPct,
    minSpreadCredit: f.minSpreadCredit === '' ? null : parseFloat(f.minSpreadCredit) / (MULTIPLIER[f.symbol] || 5),
    profitCloseMode: f.profitCloseMode, postProfitCooldownHours: f.postProfitCooldownHours,
    lossCloseMode: f.lossCloseMode, stopLossMinDte: f.stopLossMinDte,
    creditDegradationGate: { enabled: f.creditGateEnabled, maxPctBelowSignal: f.creditGateMaxPct, onBreach: f.creditGateOnBreach },
    regimeGateEnabled: f.regimeGateEnabled,
    consecutiveLossPause: { enabled: f.clpEnabled, threshold: f.clpThreshold },
    driftGate: { maxUnderlyingMovePct: f.driftMaxUnderlyingMovePct, maxDeltaIncrease: f.driftMaxDeltaIncrease, maxLegRelSpread: f.driftMaxLegRelSpread },
    minCreditToWidthPct: f.minCreditToWidthPct, applyMinCredit: f.applyMinCredit,
    keyWindowMin: f.keyWindowMin, maxDataAgeMin: f.maxDataAgeMin, maxRetryDataAgeMin: f.maxRetryDataAgeMin, observeWindowSec: f.observeWindowSec,
    fillWalkSteps: f.fillWalkSteps,
  };
}

const sameVal = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of Array.from(keys)) if (!sameVal(a[k], b[k])) return false;
    return true;
  }
  return false;
};

/** Only the fields that differ from `base` (template defaults on create, current config on edit). */
export function diffOverrides(cfg: Record<string, any>, base: Record<string, any> | null): Record<string, any> {
  if (!base) return { ...cfg };
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(cfg)) if (!sameVal(v, base[k])) out[k] = v;
  return out;
}

function templateBase(meta: FormMeta | null, strategy: V2Strategy): Record<string, any> | null {
  if (!meta) return null;
  const t = meta.templates.find(x => x.strategy === strategy);
  if (!t) return null;
  const base: Record<string, any> = {};
  for (const [k, s] of Object.entries(meta.schema.fields)) if ('default' in s) base[k] = s.default;
  for (const [k, v] of Object.entries(t.config || {})) {
    base[k] = v && typeof v === 'object' && !Array.isArray(v) && base[k] && typeof base[k] === 'object' ? { ...base[k], ...v } : v;
  }
  return base;
}

type FormMeta = { schema: V2Schema; templates: V2Template[] };
type Change = { field: string; from: any; to: any; risk?: string };
type Preview = { planId: string; kind: 'create' | 'patch'; changes: Change[]; requires?: V2Requires; idemKey: string };

const fmtVal = (v: any) => (v == null ? 'default' : typeof v === 'object' ? JSON.stringify(v) : String(v));

const STATE_BADGE: Record<string, [string, string, string]> = {
  active: ['ACTIVE', '#064e3b', '#4ade80'],
  paused: ['PAUSED', '#374151', '#6b7280'],
  inactive: ['INACTIVE', '#1e3a8a', '#93c5fd'],
  retiring: ['RETIRING', '#78350f', '#fbbf24'],
  archived: ['ARCHIVED', '#374151', '#6b7280'],
};

const ACTION_HINT: Record<string, string> = {
  ACK_REQUIRED: 'Accept the strategy risk acknowledgement in Profile > Experience & Risk, then try again.',
  TIER: 'Your experience level in Profile > Experience & Risk does not allow this strategy.',
  BROKER_TIER: 'Your TastyTrade account options level is too low for this strategy.',
};

function apiErrorLines(e: any): string[] {
  const errs = e instanceof V2ApiError && e.detail && Array.isArray(e.detail.errors) ? e.detail.errors : null;
  if (errs && errs.length) return errs.map((x: any) => (x && x.message) || String(x));
  return [e?.message || 'Request failed'];
}

const OptionsEnginesV2: React.FC = () => {
  const { user, token } = useAuth();
  const [engines, setEngines] = useState<V2Engine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ ...defaultForm });
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [meta, setMeta] = useState<FormMeta | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const enabled = canAccessAutoTrader(user, 'tastytrade');
  const ttConnected = !!(user as any)?.tastyTradeConnected;
  const ttAccount: string = (user as any)?.tastyTradeAccountNumber || '';
  const ttConnectionsRaw = (user as any)?.tastyTradeConnections;
  const ttConnections = React.useMemo(() => ttConnectionsRaw || [], [ttConnectionsRaw]);

  // v2 preview needs the real account number (the server re-verifies it against your connections).
  const connections = React.useMemo(() => {
    const conns: { connectionId: string; label: string; accountNumber: string }[] = [];
    if (ttConnections.length > 0) {
      ttConnections.forEach((c: any) => { if (c.accountNumber) conns.push({ connectionId: c.connectionId, label: c.label || 'Account', accountNumber: c.accountNumber }); });
    } else if (ttConnected && ttAccount) {
      conns.push({ connectionId: 'legacy', label: 'Main Account', accountNumber: ttAccount });
    }
    return conns;
  }, [ttConnections, ttConnected, ttAccount]);

  const loadEngines = () => {
    if (!token || !enabled) return;
    setLoading(true);
    listEngines(token).then(setEngines).catch(e => setError(e.message)).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadEngines, [token, enabled]);

  const ensureMeta = () => {
    if (meta || !token) return;
    Promise.all([getSchema(token), getTemplates(token)])
      .then(([schema, templates]) => setMeta({ schema, templates }))
      .catch(() => { /* fall back to sending every field as an override */ });
  };

  const resetForm = () => {
    setForm({ ...defaultForm });
    setShowCreate(false);
    setEditingId(null);
    setPreview(null);
    setFormErrors([]);
    setShowAdvanced(false);
  };

  // Any change invalidates the previewed plan (like v1 unticking the disclaimer).
  const setFormAndResetPreview = (updater: (f: FormState) => FormState) => {
    setForm(updater);
    setPreview(null);
    setFormErrors([]);
  };

  const editingEngine = engines.find(e => e.engineId === editingId) || null;

  const runPreview = async () => {
    if (!token) return;
    setSaving(true);
    setFormErrors([]);
    setNotice('');
    try {
      const cfg = toConfig(form);
      if (editingEngine) {
        const overrides = diffOverrides(cfg, editingEngine.effectiveConfig || {});
        const newName = form.name.trim();
        const r = await previewPatch(token, editingEngine.engineId, overrides, newName !== (editingEngine.name || '') ? newName : undefined);
        const fields = meta?.schema.fields || {};
        const changes = Object.entries(r.diffFromCurrent || {}).map(([field, d]) => ({ field, from: d.from, to: d.to, risk: fields[field]?.risk }));
        if (r.nameChange) changes.unshift({ field: 'name', from: r.nameChange.from ?? '(none)', to: r.nameChange.to ?? '(none)', risk: 'neutral' as const });
        setPreview({ planId: r.planId, kind: 'patch', changes, idemKey: '' });
      } else {
        const accountNumber = selectedAccount || connections[0]?.accountNumber;
        if (!accountNumber) { setFormErrors(['No TastyTrade accounts connected. Go to Profile to connect.']); setSaving(false); return; }
        const { strategy } = encodeTradeType(form.tradeType);
        const overrides = diffOverrides(cfg, templateBase(meta, strategy));
        const r = await previewCreate(token, { accountNumber, symbol: form.symbol, strategy, name: form.name.trim() || undefined, overrides });
        const changes = Object.entries(r.diffFromTemplate || {}).map(([field, d]) => ({ field, from: d.template, to: d.value, risk: d.risk }));
        const idemKey = `web-${String(r.planId).replace(/[^A-Za-z0-9_.:-]/g, '')}`.slice(0, 128);
        setPreview({ planId: r.planId, kind: 'create', changes, requires: r.requires, idemKey });
      }
    } catch (e: any) { setFormErrors(apiErrorLines(e)); }
    setSaving(false);
  };

  const runApply = async () => {
    if (!token || !preview) return;
    setSaving(true);
    setFormErrors([]);
    try {
      if (preview.kind === 'patch' && editingId) {
        await applyPatch(token, editingId, preview.planId);
        setNotice('Changes saved.');
      } else {
        await applyCreate(token, preview.planId, preview.idemKey);
        setNotice('Engine created. New engines start INACTIVE: click Activate on its card to start paper trading.');
      }
      resetForm();
      loadEngines();
    } catch (e: any) {
      if (e instanceof V2ApiError && (e.code === 'PLAN_NOT_FOUND' || e.code === 'PLAN_STALE')) setPreview(null);
      setFormErrors(apiErrorLines(e));
    }
    setSaving(false);
  };

  const handleEdit = (engine: V2Engine) => {
    setForm(fromConfig(engine.effectiveConfig || {}, engine));
    setEditingId(engine.engineId);
    setShowCreate(false);
    setPreview(null);
    setFormErrors([]);
    setShowAdvanced(false);
    ensureMeta();
  };

  const runAction = async (engine: V2Engine, label: string, fn: (t: string, id: string) => Promise<any>) => {
    if (!token) return;
    setBusyId(engine.engineId);
    setError('');
    setNotice('');
    try { await fn(token, engine.engineId); loadEngines(); } catch (e: any) {
      const hint = e instanceof V2ApiError && e.code ? ACTION_HINT[e.code] : '';
      setError(`${label} failed: ${e.message}${hint ? ` -- ${hint}` : ''}`);
    }
    setBusyId(null);
  };

  const handleDelete = (engine: V2Engine) => {
    if (!window.confirm('Delete this engine? This cannot be undone.')) return;
    runAction(engine, 'Delete', deleteEngine);
  };

  const badge = (label: string, bg: string, fg: string) => (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color: fg, marginRight: 6 }}>{label}</span>
  );

  const renderPreview = () => {
    if (!preview) return null;
    const riskUp = preview.changes.filter(c => c.risk === 'up');
    const warnings: string[] = [];
    if (riskUp.length) warnings.push(`Raises risk vs ${preview.kind === 'create' ? 'template defaults' : 'current settings'}: ${riskUp.map(c => c.field).join(', ')}.`);
    const req = preview.requires;
    if (req?.acks && req.acks.length) warnings.push(`Activating this engine needs your risk acknowledgement (${req.acks.join(', ')}). Set it in Profile > Experience & Risk.`);
    if (req?.persona) warnings.push(`Requires experience level: ${req.persona}.`);
    if (req?.naked) warnings.push('This strategy has an uncovered short leg: downside is not capped.');
    return (
      <div data-testid="v2-preview" style={{ marginBottom: 14 }}>
        <div style={{ padding: '10px 14px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, marginBottom: 10 }}>
          <div style={{ fontSize: 13, color: '#cbd5e1', fontWeight: 600, marginBottom: 6 }}>Preview</div>
          {preview.changes.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{preview.kind === 'create' ? 'No changes from the template defaults.' : 'No changes.'}</div>
          ) : (
            preview.changes.map(c => (
              <div key={c.field} style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>
                {c.field}: {fmtVal(c.from)} {'->'} <span style={{ color: c.risk === 'up' ? '#fbbf24' : '#e5e7eb' }}>{fmtVal(c.to)}</span>
              </div>
            ))
          )}
        </div>
        {warnings.map((w, i) => <div key={i} style={warnBanner}>{w}</div>)}
      </div>
    );
  };

  const numInput = (key: keyof FormState, min: number, max: number, step: number | string = 1, int = true) => (
    <input style={inputStyle} type="number" min={min} max={max} step={step} value={form[key] as number}
      onChange={e => {
        const v = int ? parseInt(e.target.value) : parseFloat(e.target.value);
        setFormAndResetPreview(f => ({ ...f, [key]: Number.isFinite(v) ? v : (defaultForm[key] as number) }));
      }} />
  );

  const renderAdvanced = () => (
    <div style={{ marginBottom: 14, padding: 12, border: '1px solid #374151', borderRadius: 6, background: '#1f2937' }}>
      <button type="button" onClick={() => setShowAdvanced(s => !s)}
        style={{ background: 'transparent', border: 'none', color: '#e5e7eb', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
        {showAdvanced ? '▾' : '▸'} Advanced (v2)
      </button>
      {showAdvanced && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Regime Gate</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}>
                <input type="checkbox" checked={form.regimeGateEnabled} onChange={e => setFormAndResetPreview(f => ({ ...f, regimeGateEnabled: e.target.checked }))} />
                <span style={{ color: form.regimeGateEnabled ? '#4ade80' : '#9ca3af', fontSize: 13 }}>{form.regimeGateEnabled ? 'Enabled' : 'Disabled'}</span>
              </label>
              <div style={helpStyle}>Skip entries when the market regime is unsuitable for this strategy.</div>
            </div>
            <div>
              <label style={labelStyle}>Consecutive-Loss Pause</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}>
                <input type="checkbox" checked={form.clpEnabled} onChange={e => setFormAndResetPreview(f => ({ ...f, clpEnabled: e.target.checked }))} />
                <span style={{ color: form.clpEnabled ? '#4ade80' : '#9ca3af', fontSize: 13 }}>{form.clpEnabled ? `Pause after ${form.clpThreshold} losses in a row` : 'Disabled'}</span>
              </label>
              {form.clpEnabled && numInput('clpThreshold', 1, 20)}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Drift: max underlying move (%)</label>
              {numInput('driftMaxUnderlyingMovePct', 0, 20, 0.1, false)}
            </div>
            <div>
              <label style={labelStyle}>Drift: max delta increase</label>
              {numInput('driftMaxDeltaIncrease', 0, 1, 0.01, false)}
            </div>
            <div>
              <label style={labelStyle}>Drift: max leg bid/ask spread</label>
              {numInput('driftMaxLegRelSpread', 0, 2, 0.05, false)}
              <div style={helpStyle}>Relative to mid (0.2 = 20%).</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Min Credit-to-Width (%)</label>
              {numInput('minCreditToWidthPct', 0, 100, 1, false)}
              <div style={helpStyle}>0 = off. Skip spreads whose credit is below this % of the width.</div>
            </div>
            <div>
              <label style={labelStyle}>Apply Min Credit</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}>
                <input type="checkbox" checked={form.applyMinCredit} onChange={e => setFormAndResetPreview(f => ({ ...f, applyMinCredit: e.target.checked }))} />
                <span style={{ color: form.applyMinCredit ? '#4ade80' : '#9ca3af', fontSize: 13 }}>{form.applyMinCredit ? 'Enabled' : 'Disabled'}</span>
              </label>
              <div style={helpStyle}>When off, the Min Credit floor is not enforced.</div>
            </div>
            <div>
              <label style={labelStyle}>Fill Walk Steps</label>
              {numInput('fillWalkSteps', 0, 20)}
              <div style={helpStyle}>Paper fill price-improvement steps.</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Key Window (min)</label>
              {numInput('keyWindowMin', 5, 1440)}
              <div style={helpStyle}>Readings inside this window count as the same signal.</div>
            </div>
            <div>
              <label style={labelStyle}>Max Data Age (min)</label>
              {numInput('maxDataAgeMin', 5, 1440)}
              <div style={helpStyle}>Skip readings older than this on first attempt.</div>
            </div>
            <div>
              <label style={labelStyle}>Max Retry Data Age (min)</label>
              {numInput('maxRetryDataAgeMin', 5, 10080)}
              <div style={helpStyle}>Stop retrying a reading once it is this old.</div>
            </div>
            <div>
              <label style={labelStyle}>Observe Window (sec)</label>
              {numInput('observeWindowSec', 0, 120)}
              <div style={helpStyle}>How long to watch live quotes before a paper fill.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderForm = (isEdit: boolean) => {
    const strategyLocked = isEdit && editingEngine != null && editingEngine.strategy !== 'naked_put';
    const isNakedPutFamily = !['bear_call_spread', 'jade_lizard', 'iron_condor'].includes(form.tradeType);
    return (
      <div style={{ ...cardStyle, border: '1px solid #6d28d9' }}>
        <h3 style={{ color: '#f9fafb', margin: '0 0 16px 0' }}>{isEdit ? 'Edit Engine' : 'Create Options Engine'}</h3>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Engine Name</label>
          <input style={inputStyle} value={form.name} maxLength={100} onChange={e => setFormAndResetPreview(f => ({ ...f, name: e.target.value }))} placeholder="e.g., ES 7-Day Spreads" />
          <div style={helpStyle}>Display label only; does not change how the engine trades. Leave blank for the default label.</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Symbol</label>
            <select style={selectStyle} value={form.symbol} onChange={e => setFormAndResetPreview(f => ({ ...f, symbol: e.target.value }))} disabled={isEdit}>
              {SYMBOLS.map(s => <option key={s.value} value={s.value}>{s.label} ({s.multiplier})</option>)}
              {isEdit && !SYMBOLS.some(s => s.value === form.symbol) && <option value={form.symbol}>{form.symbol}</option>}
            </select>
            {isEdit && <div style={helpStyle}>Symbol cannot be changed</div>}
          </div>
          <div>
            <label style={labelStyle}>Trade Type</label>
            <select style={selectStyle} value={form.tradeType} disabled={strategyLocked} onChange={e => setFormAndResetPreview(f => ({ ...f, tradeType: e.target.value as TradeType }))}>
              <option value="spread">Credit Spread (defined risk)</option>
              <option value="naked">Naked Put (higher premium)</option>
              <option value="auto">Auto (smart selection)</option>
              <option value="bear_call_spread" disabled={isEdit}>Bear Call Spread (bearish, defined risk)</option>
              <option value="jade_lizard" disabled={isEdit}>Jade Lizard (bullish, 3 legs)</option>
              <option value="iron_condor" disabled={isEdit}>Iron Condor (neutral, 4 legs, defined risk)</option>
            </select>
            <div style={{ fontSize: 11, color: form.tradeType === 'naked' ? '#f59e0b' : form.tradeType === 'auto' ? '#3b82f6' : form.tradeType === 'bear_call_spread' ? '#a855f7' : form.tradeType === 'jade_lizard' ? '#f59e0b' : form.tradeType === 'iron_condor' ? '#22d3ee' : '#6b7280', marginTop: 2 }}>
              {form.tradeType === 'naked' ? 'Uncapped downside — margin required'
                : form.tradeType === 'auto' ? 'Chooses spread vs naked based on IV, delta, and liquidity each run'
                : form.tradeType === 'bear_call_spread' ? 'Bearish call credit spread. Strikes + width come from the BCS signal generator.'
                : form.tradeType === 'jade_lizard' ? 'Bullish 3-leg (short put + short call + long call). Generator enforces no upside risk. Downside is uncapped (short put).'
                : form.tradeType === 'iron_condor' ? 'Neutral 4-leg (long put + short put + short call + long call). Defined risk on both wings.'
                : 'Max loss = spread width − credit'}
            </div>
            {isEdit && <div style={helpStyle}>{strategyLocked ? 'Strategy cannot be changed' : 'Strategy family cannot be changed'}</div>}
          </div>
          <div data-testid="v2-mode-field">
            <label style={labelStyle}>Mode</label>
            {/* Mode is not a config field: it changes only through the mode endpoint (card button), so the form shows it read-only. */}
            <div style={{ ...selectStyle, background: '#111827', color: '#e5e7eb' }}>
              {isEdit && editingEngine?.mode === 'live' ? 'Live (real money)' : 'Paper (simulated fills)'}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
              {isEdit
                ? 'To change it, pause the engine and use Switch to paper / Switch to live on its card. Live needs the account enabled for live trading; live to paper needs the engine flat.'
                : 'New engines start in Paper: real market data and Greeks via your TastyTrade connection, with simulated broker orders.'}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Notches Preset</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['conservative', 'balanced', 'aggressive'] as NotchesPreset[]).map((p) => {
              const active = form.preset === p;
              return (
                <button key={p} type="button" onClick={() => setFormAndResetPreview(f => ({ ...f, preset: p }))}
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    background: active ? '#6d28d9' : '#111827', color: active ? '#f9fafb' : '#9ca3af',
                    border: active ? '1px solid #7c3aed' : '1px solid #374151', textAlign: 'left',
                  }}>{PRESET_INFO[p].label}</button>
              );
            })}
          </div>
          <div style={{ marginTop: 8, padding: 8, background: '#0f172a', borderRadius: 4, fontSize: 12, color: '#94a3b8' }}>
            {PRESET_INFO[form.preset].short}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Notches min DTE</label>
            <input style={inputStyle} type="number" min={0} max={400} value={form.minDte}
              onChange={e => setFormAndResetPreview(f => ({ ...f, minDte: parseInt(e.target.value) || 0 }))} />
            <div style={helpStyle}>Skip candidates with fewer days to expiration than this (0 = no floor).</div>
          </div>
          <div>
            <label style={labelStyle}>Notches max DTE</label>
            <input style={inputStyle} type="number" min={0} max={400} value={form.maxDte}
              onChange={e => setFormAndResetPreview(f => ({ ...f, maxDte: parseInt(e.target.value) || 60 }))} />
            <div style={helpStyle}>Skip candidates beyond this DTE. Default 60.</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
          {isNakedPutFamily && form.tradeType !== 'naked' && <div>
            <label style={labelStyle}>Spread Width (pts)</label>
            <input style={inputStyle} type="number" min={1} max={200} value={form.spreadWidth} onChange={e => setFormAndResetPreview(f => ({ ...f, spreadWidth: parseInt(e.target.value) || 50 }))} />
          </div>}
          <div>
            <label style={labelStyle}>Contracts</label>
            <input style={inputStyle} type="number" value={form.quantity} min={1} max={50} onChange={e => setFormAndResetPreview(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
          </div>
          <div>
            <label style={labelStyle}>Max Open Positions</label>
            <input style={inputStyle} type="number" value={form.maxOpenPositions} min={1} max={50} onChange={e => setFormAndResetPreview(f => ({ ...f, maxOpenPositions: parseInt(e.target.value) || 3 }))} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Profit Target (%)</label>
            <input style={inputStyle} type="number" value={form.profitTargetPct} min={10} max={90} onChange={e => setFormAndResetPreview(f => ({ ...f, profitTargetPct: parseInt(e.target.value) || 50 }))} />
            <div style={helpStyle}>Close after capturing {form.profitTargetPct}% of credit (e.g. 75 = wait until 3/4 of premium captured)</div>
          </div>
          <div>
            <label style={labelStyle}>Stop Loss (multiplier)</label>
            <input style={inputStyle} type="number" step="0.5" value={form.stopLossMultiplier} min={0} max={10} onChange={e => setFormAndResetPreview(f => ({ ...f, stopLossMultiplier: parseFloat(e.target.value) || 2 }))} />
            <div style={helpStyle}>Close when spread reaches {form.stopLossMultiplier}x credit</div>
          </div>
          <div>
            <label style={labelStyle}>Hard Stop (multiplier)</label>
            <input style={inputStyle} type="number" step="0.5" value={form.hardStopMultiplier} min={0} max={20}
              onChange={e => setFormAndResetPreview(f => ({ ...f, hardStopMultiplier: Math.max(0, parseFloat(e.target.value) || 0) }))} />
            <div style={helpStyle}>
              {form.hardStopMultiplier > 0
                ? `Unconditional close when spread reaches ${form.hardStopMultiplier}x credit — bypasses lossCloseMode=alerts. Only applies to NEW trades opened after saving.`
                : '0 = disabled. Set > 0 (e.g. 3) to enable a hard-stop tier that closes regardless of alerts mode.'}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Underlying Hard Stop</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}>
              <input type="checkbox" checked={form.enableHardStopUnderlying} onChange={e => setFormAndResetPreview(f => ({ ...f, enableHardStopUnderlying: e.target.checked }))} />
              <span style={{ color: form.enableHardStopUnderlying ? '#4ade80' : '#9ca3af', fontSize: 13 }}>
                {form.enableHardStopUnderlying ? `Enabled (${Math.round(form.hardStopUnderlyingBuffer * 100)}% buffer)` : 'Disabled'}
              </span>
            </label>
            {form.enableHardStopUnderlying && (
              <input style={{ ...inputStyle, marginTop: 6 }} type="number" step="0.005" value={form.hardStopUnderlyingBuffer} min={0.005} max={0.20}
                onChange={e => {
                  const v = parseFloat(e.target.value);
                  setFormAndResetPreview(f => ({ ...f, hardStopUnderlyingBuffer: (Number.isFinite(v) && v > 0 && v < 0.5) ? v : 0.02 }));
                }} />
            )}
            <div style={helpStyle}>
              {form.enableHardStopUnderlying
                ? `Force-close when spot crosses sellStrike ± ${Math.round(form.hardStopUnderlyingBuffer * 100)}% (put-side ⇒ below, call-side ⇒ above). Bypasses lossCloseMode=alerts. Only applies to NEW trades.`
                : 'When on, stamps a spot-price threshold on new trades. Ignores IV / bid-ask blowouts — cleanest defense against violent underlying moves.'}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Max Daily Trades</label>
            <input style={inputStyle} type="number" value={form.maxDailyTrades} min={1} max={50} onChange={e => setFormAndResetPreview(f => ({ ...f, maxDailyTrades: parseInt(e.target.value) || 2 }))} />
          </div>
          <div>
            <label style={labelStyle}>Max BP Usage (%)</label>
            <input style={inputStyle} type="number" value={form.maxBpPct} min={1} max={100} onChange={e => setFormAndResetPreview(f => ({ ...f, maxBpPct: parseInt(e.target.value) || 40 }))} />
            <div style={helpStyle}>Skip trades if BP usage exceeds {form.maxBpPct}%</div>
          </div>
          <div>
            <label style={labelStyle}>Min Credit ($)</label>
            <input style={inputStyle} type="number" step="5" min={0} value={form.minSpreadCredit}
              placeholder={`default $${DEFAULT_MIN_CREDIT_DOLLARS[form.symbol] ?? ''}`}
              onChange={e => setFormAndResetPreview(f => ({ ...f, minSpreadCredit: e.target.value }))} />
            <div style={helpStyle}>
              {form.minSpreadCredit
                ? `Skip trades collecting less than $${form.minSpreadCredit} credit`
                : `Using default $${DEFAULT_MIN_CREDIT_DOLLARS[form.symbol] ?? '?'}. Leave blank for default.`}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Profit Close Mode</label>
            <select style={selectStyle} value={form.profitCloseMode} onChange={e => setFormAndResetPreview(f => ({ ...f, profitCloseMode: e.target.value as ProfitCloseMode }))}>
              <option value="smart">Smart (default) — let deep-OTM near-expiry run</option>
              <option value="strict">Strict — always close at the target %</option>
            </select>
            <div style={{ fontSize: 11, color: form.profitCloseMode === 'smart' ? '#3b82f6' : '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
              {form.profitCloseMode === 'smart'
                ? `Skip the close if the short is very low delta (<0.10), the market moved favorably, the strike is deep OTM, and DTE ≤ 7 — let it expire worthless instead. Otherwise close after capturing ${form.profitTargetPct}% of credit.`
                : `Always close after capturing ${form.profitTargetPct}% of credit. No conditional hold.`}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Post-Profit Cooldown (hours)</label>
            <input style={inputStyle} type="number" min={0} max={168} value={form.postProfitCooldownHours}
              onChange={e => setFormAndResetPreview(f => ({ ...f, postProfitCooldownHours: Math.max(0, Math.min(168, parseInt(e.target.value) || 0)) }))} />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
              {form.postProfitCooldownHours > 0
                ? `After a profit close, pause new opens for ${form.postProfitCooldownHours}h. IV is typically crushed right after a winning close — selling new premium then is selling at the local IV-low.`
                : 'No cooldown — new trades can open immediately after a profit close. Set 1-24h to wait for IV to normalize.'}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20, padding: '12px 14px', border: `1px solid ${form.lossCloseMode === 'alerts' ? '#fbbf24' : '#374151'}`, borderRadius: 8, background: form.lossCloseMode === 'alerts' ? '#78350f22' : '#111827' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Stop-Loss Action</label>
              <select style={selectStyle} value={form.lossCloseMode} onChange={e => setFormAndResetPreview(f => ({ ...f, lossCloseMode: e.target.value as LossCloseMode }))}>
                <option value="auto">Auto-close on stop-loss (default)</option>
                <option value="alerts">Alerts only — never auto-close on loss</option>
              </select>
              <div style={{ fontSize: 11, color: form.lossCloseMode === 'alerts' ? '#fbbf24' : '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
                {form.lossCloseMode === 'alerts'
                  ? 'Stop-loss triggers send an SMS and log an audit entry, but the position is HELD — you close manually if needed. Profit-target closes still fire automatically.'
                  : `Auto-close on stop-loss triggers (${form.stopLossMultiplier}× entry credit or ITM confirmation). Standard behavior.`}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Stop-Loss Min DTE</label>
              <input style={inputStyle} type="number" min={0} max={400} value={form.stopLossMinDte}
                onChange={e => setFormAndResetPreview(f => ({ ...f, stopLossMinDte: Math.max(0, Math.min(400, parseInt(e.target.value) || 0)) }))} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
                {form.stopLossMinDte > 0
                  ? `Above ${form.stopLossMinDte} DTE, stop-loss requires |Δ|≥0.35 (the existing "high probability of ITM" bar) to fire. Below, it triggers on the ${form.stopLossMultiplier}× price threshold as usual. Prevents closing 45-DTE trades on temporary IV spikes.`
                  : 'Disabled — stop-loss fires purely on the price threshold at every DTE.'}
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 14, padding: 12, border: '1px solid #374151', borderRadius: 6, background: '#1f2937' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#e5e7eb', fontWeight: 600, marginBottom: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.creditGateEnabled} onChange={e => setFormAndResetPreview(f => ({ ...f, creditGateEnabled: e.target.checked }))} style={{ width: 16, height: 16, cursor: 'pointer' }} />
            Credit Degradation Gate {form.creditGateEnabled ? <span style={{ color: '#10b981', fontSize: 11 }}>(ON)</span> : <span style={{ color: '#9ca3af', fontSize: 11 }}>(off)</span>}
          </label>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, lineHeight: 1.5 }}>
            Immediately before the paper fill, compare the live quote credit against the strategy's reference credit (the credit the engine would have expected for THIS execution shape — naked, spread, jade, iron condor). If the live credit has dropped more than the allowed percent below reference, the opportunity has decayed and the trade is skipped. Scales correctly across all strategies.
          </div>
          {form.creditGateEnabled && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Max % below reference credit</label>
                <input style={inputStyle} type="number" step="0.1" min={0} max={100} value={form.creditGateMaxPct}
                  onChange={e => setFormAndResetPreview(f => ({ ...f, creditGateMaxPct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 1)) }))} />
                <div style={helpStyle}>Default 1%. Example: signal $1.14, max 1% → skip if live &lt; $1.1286.</div>
              </div>
              <div>
                <label style={labelStyle}>On breach</label>
                <select style={inputStyle} value={form.creditGateOnBreach} onChange={e => setFormAndResetPreview(f => ({ ...f, creditGateOnBreach: (e.target.value === 'proceed' ? 'proceed' : 'skip') }))}>
                  <option value="skip">Skip this cycle (recommended)</option>
                  <option value="proceed">Proceed with live quote</option>
                </select>
                <div style={helpStyle}>
                  {form.creditGateOnBreach === 'skip'
                    ? 'Audit-log the breach and wait for the next signal. Safest.'
                    : 'Audit-log the breach but still fill at the live (degraded) credit. Never falls back to the stale signal credit.'}
                </div>
              </div>
            </div>
          )}
        </div>

        {renderAdvanced()}

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>TastyTrade Account</label>
          {isEdit ? (
            <select style={selectStyle} value="x" disabled><option value="x">{maskAcct(editingEngine?.accountNumber)}</option></select>
          ) : connections.length > 0 ? (
            <select style={selectStyle} value={selectedAccount || connections[0].accountNumber} onChange={e => { setSelectedAccount(e.target.value); setPreview(null); }}>
              {connections.map(c => (
                <option key={c.connectionId + c.accountNumber} value={c.accountNumber}>{c.label} (***{c.accountNumber.slice(-4)})</option>
              ))}
            </select>
          ) : (
            <div style={{ fontSize: 13, color: '#f87171' }}>No TastyTrade accounts connected. Go to Profile to connect.</div>
          )}
          {isEdit && <div style={helpStyle}>Account cannot be changed after creation</div>}
        </div>

        {formErrors.map((m, i) => <div key={i} style={errorBanner}>{m}</div>)}
        {renderPreview()}

        <div style={{ display: 'flex', gap: 10 }}>
          <button style={btnPrimary} onClick={preview ? runApply : runPreview} disabled={saving || (!isEdit && connections.length === 0)}>
            {saving ? 'Saving...' : preview ? (isEdit ? 'Confirm & Save' : 'Confirm & Create') : isEdit ? 'Save Changes' : 'Create Engine'}
          </button>
          <button style={btnSecondary} onClick={resetForm}>Cancel</button>
        </div>
      </div>
    );
  };

  if (!enabled) {
    return (
      <div className="dashboard-container"><Navigation />
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
          <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700 }}>Options Auto-Trader v2</h1>
          <div style={cardStyle}><p style={{ color: '#9ca3af', textAlign: 'center' }}>This feature is not enabled on your account. Contact support to get access.</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container"><Navigation />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>
              Options Auto-Trader v2
              <span style={{ marginLeft: 10, padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700, background: '#f59e0b30', color: '#f59e0b', verticalAlign: 'middle', letterSpacing: '0.05em' }}>BETA</span>
            </h1>
            <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>Automated options trading from Notches signals via TastyTrade. Each engine runs in Paper or Live mode (shown on its card).</p>
          </div>
          {!showCreate && !editingId && (
            <div style={{ display: 'flex', gap: 8 }}>
            {connections.length > 0 && <button onClick={() => setShowManual(true)} style={btnSmall}>+ Manual Engine</button>}
            <button style={btnPrimary} onClick={() => { if (connections.length === 0) { window.location.href = '/profile'; } else { resetForm(); setNotice(''); setShowCreate(true); ensureMeta(); } }}>
              {connections.length > 0 ? '+ New Engine' : 'Connect TastyTrade First'}
            </button>
            </div>
          )}
        </div>

        {error && <div style={errorBanner}>{error}</div>}
        {notice && <div style={{ color: '#4ade80', marginBottom: 12, padding: '8px 12px', background: '#064e3b44', borderRadius: 6 }}>{notice}</div>}

        {showCreate && renderForm(false)}
        {editingId && renderForm(true)}

        {showManual && token && <ManualEngineCreate token={token} accounts={connections.map(c => c.accountNumber)} engines={engines} onCreated={e => setEngines(list => [e, ...list])} onClose={() => setShowManual(false)} />}

        {loading && <p style={{ color: '#6b7280' }}>Loading engines...</p>}

        {!loading && engines.length === 0 && !showCreate && (
          <div style={cardStyle}>
            <p style={{ color: '#9ca3af', textAlign: 'center' }}>No options trading engines yet. Click "+ New Engine" to get started.</p>
          </div>
        )}

        {engines.map(engine => {
          if (editingId === engine.engineId) return null;
          const cfg: Record<string, any> = engine.effectiveConfig || {};
          const symbol = engine.symbol || cfg.symbol || 'ES';
          const st = STATE_BADGE[engine.state] || [String(engine.state || '').toUpperCase(), '#374151', '#9ca3af'];
          const strat = strategyLabel(engine.strategy || cfg.strategy, cfg.tradeType, cfg.spreadWidth);
          const busy = busyId === engine.engineId;
          const minCreditOff = cfg.applyMinCredit === false;
          if (engine.engineType === 'manual') {
            const c = engine.manualCounts || { positions: 0, managed: 0, alertsOnly: 0, entryUnknown: 0 };
            return (
              <div key={engine.engineId} style={cardStyle} data-testid="v2-engine-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#f9fafb', fontSize: 18 }}>{engineDisplayName(engine)}</h3>
                    {badge('LIVE', '#064e3b', '#4ade80')}{badge(st[0], st[1], st[2])}{badge('MANUAL', '#1e1b4b', '#a78bfa')}
                    {engine.suspendedReason && badge('ON HOLD: v1 still active', '#78350f', '#fbbf24')}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {engine.state === 'active' && <button onClick={() => runAction(engine, 'Pause', pauseEngine)} style={btnSmall} disabled={busy}>Pause</button>}
                    {engine.state === 'paused' && <button onClick={() => runAction(engine, 'Resume', resumeEngine)} style={btnSmall} disabled={busy}>Resume</button>}
                    {engine.state !== 'archived' && <button onClick={() => runAction(engine, 'Hands off', (t: string, id: string) => setEngineManagement(t, id, engine.management === 'hands_off' ? 'auto' : 'hands_off'))} style={btnSmall} disabled={busy}>{engine.management === 'hands_off' ? 'Allow closes' : 'Hands off'}</button>}
                    {engine.state !== 'archived' && <button onClick={() => window.confirm('Retire the manual engine? Its positions go back to unmanaged.') && runAction(engine, 'Retire', retireEngine)} style={btnDanger} disabled={busy}>Retire</button>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 12, color: '#d1d5db', fontSize: 14 }}>
                  <span>{c.positions} positions</span><span>{c.managed} managed</span><span>{c.alertsOnly} alerts only</span><span>{c.entryUnknown} entry unknown</span>
                  <span style={{ color: '#6b7280' }}>Account {maskAcct(engine.accountNumber)}</span>
                </div>
                {token && <AlertSwitches token={token} engine={engine} onChanged={e => setEngines(list => list.map(x => (x.engineId === e.engineId ? { ...x, ...e } : x)))} />}
              </div>
            );
          }
          return (
            <div key={engine.engineId} style={cardStyle} data-testid="v2-engine-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: '0 0 8px 0', color: '#f9fafb', fontSize: 18 }}>{engineDisplayName(engine)}</h3>
                  <div>
                    {badge(engine.mode === 'live' ? 'LIVE' : 'PAPER', engine.mode === 'live' ? '#064e3b' : '#78350f', engine.mode === 'live' ? '#4ade80' : '#fbbf24')}
                    {badge(st[0], st[1], st[2])}
                    {badge(symbol, '#1e1b4b', '#a78bfa')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {engine.state !== 'archived' && engine.state !== 'retiring' && (
                    <button onClick={() => handleEdit(engine)} style={btnSmall} disabled={busy}>Edit</button>
                  )}
                  {engine.state === 'active' && <button onClick={() => runAction(engine, 'Pause', pauseEngine)} style={btnSmall} disabled={busy}>Pause</button>}
                  {engine.state === 'paused' && <button onClick={() => runAction(engine, 'Resume', resumeEngine)} style={btnSmall} disabled={busy}>Resume</button>}
                  {engine.state === 'inactive' && <button onClick={() => runAction(engine, 'Activate', activateEngine)} style={{ ...btnSmall, background: '#065f46' }} disabled={busy}>Activate</button>}
                  {(engine.state === 'paused' || engine.state === 'inactive') && (() => {
                    const to: 'paper' | 'live' = engine.mode === 'live' ? 'paper' : 'live';
                    const label = to === 'paper' ? 'Switch to paper' : 'Switch to live';
                    const msg = to === 'paper' ? 'Switch this engine to Paper? It must have no open live trades.' : 'Switch this engine to Live (real money)? The account must be enabled for live trading.';
                    return <button onClick={() => window.confirm(msg) && runAction(engine, label, (t: string, id: string) => setEngineMode(t, id, to))} style={btnSmall} disabled={busy}>{label}</button>;
                  })()}
                  <button onClick={() => handleDelete(engine)} style={btnDanger} disabled={busy}>Delete</button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 16 }}>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>TastyTrade Account</div><div style={{ fontSize: 14, color: '#f9fafb', fontWeight: 600 }}>{maskAcct(engine.accountNumber)}</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Signal Source</div><div style={{ fontSize: 14, color: '#8b5cf6' }}>Notches / {cfg.preset || 'conservative'}</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>DTE Range</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.minDte ?? 0}-{cfg.maxDte ?? 60}d</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Strategy</div><div style={{ fontSize: 14, color: strat.color }}>{strat.label}</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Contracts</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.quantity || 1}</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Profit Target</div><div style={{ fontSize: 14, color: '#4ade80' }}>{cfg.profitTargetPct || 50}%</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Stop Loss</div><div style={{ fontSize: 14, color: '#f87171' }}>{cfg.stopLossMultiplier ?? 2}x</div></div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>Hard Stop</div>
                  <div style={{ fontSize: 14, color: (cfg.hardStopMultiplier || 0) > 0 ? '#4ade80' : '#6b7280', fontWeight: (cfg.hardStopMultiplier || 0) > 0 ? 600 : 400 }}>
                    {(cfg.hardStopMultiplier || 0) > 0 ? `${cfg.hardStopMultiplier}x` : 'off'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>Underlying Stop</div>
                  <div style={{ fontSize: 14, color: cfg.enableHardStopUnderlying ? '#4ade80' : '#6b7280', fontWeight: cfg.enableHardStopUnderlying ? 600 : 400 }}>
                    {cfg.enableHardStopUnderlying ? `${Math.round((cfg.hardStopUnderlyingBuffer ?? 0.02) * 100)}%` : 'off'}
                  </div>
                </div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Max Positions</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.maxOpenPositions || 3}</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Max Daily</div><div style={{ fontSize: 14, color: '#f9fafb' }}>{cfg.maxDailyTrades || 2}</div></div>
                <div><div style={{ color: '#6b7280', fontSize: 11 }}>Max BP%</div><div style={{ fontSize: 14, color: '#fbbf24' }}>{cfg.maxBpPct || 40}%</div></div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>Min Credit</div>
                  <div style={{ fontSize: 14, color: minCreditOff ? '#6b7280' : cfg.minSpreadCredit != null ? '#a78bfa' : '#9ca3af' }}>
                    {minCreditOff ? 'off' : <>
                      ${cfg.minSpreadCredit != null ? Math.round(cfg.minSpreadCredit * (MULTIPLIER[symbol] || 5)) : (DEFAULT_MIN_CREDIT_DOLLARS[symbol] ?? '?')}
                      {cfg.minSpreadCredit == null && <span style={{ fontSize: 10, marginLeft: 4 }}>(default)</span>}
                    </>}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>Close Mode</div>
                  <div style={{ fontSize: 14, color: (cfg.profitCloseMode || 'smart') === 'strict' ? '#fbbf24' : '#a5b4fc' }}>{(cfg.profitCloseMode || 'smart') === 'strict' ? 'Strict' : 'Smart'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>Post-Profit Cooldown</div>
                  <div style={{ fontSize: 14, color: (cfg.postProfitCooldownHours ?? 0) > 0 ? '#a78bfa' : '#9ca3af' }}>{(cfg.postProfitCooldownHours ?? 0) > 0 ? `${cfg.postProfitCooldownHours}h` : 'Off'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>Stop-Loss Action</div>
                  <div style={{ fontSize: 14, color: (cfg.lossCloseMode || 'auto') === 'alerts' ? '#fbbf24' : '#f87171' }}>{(cfg.lossCloseMode || 'auto') === 'alerts' ? 'Alerts only' : 'Auto-close'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>Stop-Loss Min DTE</div>
                  <div style={{ fontSize: 14, color: (cfg.stopLossMinDte ?? 21) > 0 ? '#a78bfa' : '#9ca3af' }}>{(cfg.stopLossMinDte ?? 21) > 0 ? `${cfg.stopLossMinDte ?? 21}d` : 'Off'}</div>
                </div>
              </div>
              {token && <AlertSwitches token={token} engine={engine} onChanged={e => setEngines(list => list.map(x => (x.engineId === e.engineId ? { ...x, ...e } : x)))} />}
            </div>
          );
        })}
      </div>
      <RiskDisclaimer />
    </div>
  );
};

export default OptionsEnginesV2;
